import { Flex } from '@mantine/core';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import CopyActionButton from '@/components/shared/CopyActionButton';
import PrintActionButton from '@/components/shared/PrintActionButton';
import ToolCallHistoryLink from '@/components/shared/ToolCallHistoryLink';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import { useCopyFormattedText } from '@/hooks/useCopyFormattedText';
import { usePlainTextContent } from '@/hooks/usePlainTextContent';
import { usePrint } from '@/hooks/usePrint';
import type { ChatMessage } from '@/stores/local/pages/OrganizationChatAiPageStore/types';
import { useDevices } from '@/utils/devices';

import RatingPanel from '../RatingPanel';

import s from './AiAnswer.module.scss';

type Props = {
  text?: string;
  isLoadingAnswer?: boolean;
  answerId?: string;
  status?: ChatMessage['status'];
  activitySummary?: string;
  onActivityOpen?: () => void;
};

const AiAnswer: React.FC<Props> = ({ text, isLoadingAnswer, answerId, status, activitySummary, onActivityOpen }) => {
  const { isCompactViewport } = useDevices();

  const { id: organizationId } = useParams();
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const hasText = Boolean(text?.trim());
  const canShowRating = !isLoadingAnswer && hasText && Boolean(answerId) && (status === 'done' || status === undefined);
  const canShowActions = !isLoadingAnswer && hasText && status === 'done';
  const canShowActivity = status === 'done' && Boolean(activitySummary);
  const getPlainText = usePlainTextContent(contentRef, text ?? '');
  const getHtmlContent = React.useCallback(() => contentRef.current?.innerHTML ?? '', []);
  const handlePrint = usePrint(getPlainText, {
    title: 'Ответ ассистента',
    getHtml: getHtmlContent,
    organizationId,
  });
  const handleCopy = useCopyFormattedText(getHtmlContent, getPlainText);

  return (
    <Flex className={s.root} direction="column" gap="lg">
      <Flex gap="md" direction="column">
        <div ref={contentRef}>
          <MarkdownRenderer content={text} />
        </div>
        {canShowActions && (
          <Flex gap="xs" align="center">
            <CopyActionButton
              variant={isCompactViewport ? 'light' : 'subtle'}
              color={isCompactViewport ? 'blue' : 'gray'}
              onCopy={handleCopy}
              aria-label="Скопировать ответ"
              size={isCompactViewport ? 'lg' : 'md'}
              iconSize={18}
            />
            {isCompactViewport && <RatingPanel answerId={answerId} />}
            {!isCompactViewport && (
              <PrintActionButton variant="subtle" color="gray" onClick={handlePrint} size="md" iconSize={18} />
            )}
          </Flex>
        )}
        {canShowActivity && activitySummary && (
          <ToolCallHistoryLink label={activitySummary} onClick={onActivityOpen} withArrow />
        )}
      </Flex>
      {canShowRating && !isCompactViewport && <RatingPanel className={s.root__rating} answerId={answerId} />}
    </Flex>
  );
};

export default React.memo(AiAnswer);
